/**
 * HeaderStatusPill.tsx — MER Algo Footprints v9.2.0
 *
 * Phase 2: Global Trading Header.
 *
 * Color-coded chip used throughout the HUD for status indicators (legend,
 * posture, AI mode, market state, etc.). Pure presentational — no hooks.
 *
 * Color tokens map to Phase 2 spec Step 8 CSS variables:
 *   --mer-green   --mer-red   --mer-yellow   --mer-purple   --mer-muted
 */

import React from 'react';

export type PillTone =
  | 'green'    // bullish, ON, healthy
  | 'red'      // bearish, OFF, error
  | 'yellow'   // warning, stale, caution
  | 'purple'   // AI activity, premium, special
  | 'muted'    // neutral, no data
  | 'plain';   // unstyled — text only

export interface HeaderStatusPillProps {
  tone?:    PillTone;
  label:    string;
  title?:   string;        // tooltip
  uppercase?: boolean;
  size?:    'sm' | 'md';   // sm=10px (legend, strip), md=12px (Row 2)
  dot?:     boolean;       // leading status dot
}

export function HeaderStatusPill({
  tone = 'plain',
  label,
  title,
  uppercase = true,
  size = 'sm',
  dot = false,
}: HeaderStatusPillProps) {
  const palette: Record<PillTone, { color: string; bg: string; border: string }> = {
    green:  { color: 'var(--mer-green)',  bg: 'rgba(24,245,162,0.10)',  border: 'rgba(24,245,162,0.32)'  },
    red:    { color: 'var(--mer-red)',    bg: 'rgba(255,77,109,0.10)',  border: 'rgba(255,77,109,0.32)'  },
    yellow: { color: 'var(--mer-yellow)', bg: 'rgba(255,204,77,0.10)',  border: 'rgba(255,204,77,0.32)'  },
    purple: { color: 'var(--mer-purple)', bg: 'rgba(166,108,255,0.10)', border: 'rgba(166,108,255,0.32)' },
    muted:  { color: 'var(--mer-muted)',  bg: 'rgba(138,162,200,0.06)', border: 'rgba(138,162,200,0.22)' },
    plain:  { color: 'var(--mer-text)',   bg: 'transparent',            border: 'transparent'            },
  };
  const c = palette[tone];

  const fontSize = size === 'md' ? 12 : 10;
  const padV = size === 'md' ? 4 : 2;
  const padH = size === 'md' ? 10 : 8;

  return (
    <span
      className="mer-pill"
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: `${padV}px ${padH}px`,
        borderRadius: 4,
        fontSize,
        lineHeight: 1.4,
        fontWeight: 600,
        letterSpacing: uppercase ? 0.6 : 0.2,
        textTransform: uppercase ? 'uppercase' : 'none',
        color: c.color,
        background: c.bg,
        border: `1px solid ${c.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: c.color,
            boxShadow: `0 0 6px ${c.color}`,
          }}
        />
      )}
      {label}
    </span>
  );
}
